import React from 'react';
import { Card, CardBody } from '../components/ui';
import { useAuth } from './AuthProvider';

function authStateFromLocation(): string | null {
  const params = new URLSearchParams(window.location.search);
  return params.get('auth');
}

export function AuthGate(props: { children: React.ReactNode }) {
  const { loading, user, authError, login } = useAuth();
  const authState = authStateFromLocation();
  const [redirecting, setRedirecting] = React.useState(false);
  const isPublicRoute = window.location.pathname.replace(/\/+$/, '') === '/qrlander';
  const hasAccess = Boolean(user);
  const isAuthError = Boolean(authState);
  const isDenied = authState === 'not_entitled';
  const hasProviderAuthError = Boolean(authError);

  React.useEffect(() => {
    if (isPublicRoute) return;
    if (loading || hasAccess || isAuthError || hasProviderAuthError) return;
    if (redirecting) return;
    setRedirecting(true);
    login();
  }, [isPublicRoute, loading, hasAccess, isAuthError, hasProviderAuthError, redirecting, login]);

  if (isPublicRoute) {
    return <>{props.children}</>;
  }

  if (hasAccess) {
    return <>{props.children}</>;
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-2 p-6">
            <div className="text-sm font-semibold text-zinc-900">Checking session</div>
            <div className="text-sm text-zinc-600">Validating your Wild Apricot sign-in.</div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (isDenied) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-2 p-6">
            <div className="text-sm font-semibold text-zinc-900">Access Denied</div>
            <div className="text-sm text-zinc-600">
              You cannot access the Hub until your membership is assigned by the Syteline User Group office. You'll
              receive an email when this is complete.
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (isAuthError) {
    const isInvalidState = authState === 'invalid_state';
    const title = isInvalidState ? 'Sign-In Failed' : 'Authentication Error';
    const message = isInvalidState
      ? 'State validation failed. Open the app in a fresh tab and try again.'
      : 'Wild Apricot authentication did not complete.';

    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-2 p-6">
            <div className="text-sm font-semibold text-zinc-900">{title}</div>
            <div className="text-sm text-zinc-600">{message}</div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (hasProviderAuthError) {
    const isFirebaseTokenError = authError === 'firebase_token_unavailable';
    const title = isFirebaseTokenError ? 'Sign-In Configuration Error' : 'Authentication Error';
    const message = isFirebaseTokenError
      ? 'Wild Apricot sign-in succeeded, but Firebase token minting failed. Check function service-account IAM for iam.serviceAccounts.signBlob.'
      : 'Session validation failed unexpectedly. Please try again shortly.';
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4">
        <Card className="w-full max-w-md">
          <CardBody className="space-y-2 p-6">
            <div className="text-sm font-semibold text-zinc-900">{title}</div>
            <div className="text-sm text-zinc-600">{message}</div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-4">
      <Card className="w-full max-w-md">
        <CardBody className="space-y-2 p-6">
          <div className="text-sm font-semibold text-zinc-900">Redirecting</div>
          <div className="text-sm text-zinc-600">Signing in via Wild Apricot.</div>
        </CardBody>
      </Card>
    </div>
  );
}
